import ApiClient from 'api-client/ApiClient'
import { sleep } from 'api-client/helpers'

// =============================================================================
// Install-from-ISO boot volume firmware metadata (PCD-8237, IBV-FR-001..005)
//
// The Install-from-ISO wizard used to let Nova build a *blank* boot volume, which
// carries no image metadata, so the VM booted legacy BIOS with no Secure Boot / TPM
// and Windows 11 / Server 2025 refused to install. Here we pre-create the boot
// volume, copy the installer ISO's firmware-relevant image properties onto it, then
// the VM boots from that volume so Nova reads UEFI / Secure Boot / vTPM from it.
// =============================================================================

/**
 * IBV-FR-001: the only image properties we copy from the installer ISO onto the boot
 * volume. Kept to an allowlist on purpose so we never copy volume locations, hashes,
 * signatures, or the hidden flag.
 */
export const ISO_FIRMWARE_METADATA_KEYS = [
  'hw_firmware_type',
  'hw_machine_type',
  'os_secure_boot',
  'hw_tpm_version',
  'hw_tpm_model',
  'os_type',
] as const

const DEFAULT_TIMEOUT_MS = 900000 // 15 minutes, matches the ISO mount flow for large volumes
const DEFAULT_POLL_INTERVAL_MS = 5000

/**
 * IBV-FR-001 / SC-004: pick the allowlisted firmware properties that are actually
 * present on the installer ISO. Glance flattens custom properties onto the image, but
 * some shapes nest them under `properties`, so we check both. Absent or empty values
 * are skipped so we never send an empty/invalid value to Cinder.
 */
export const pickIsoFirmwareMetadata = (iso: any): Record<string, string> => {
  const metadata: Record<string, string> = {}
  if (!iso) {
    return metadata
  }
  for (const key of ISO_FIRMWARE_METADATA_KEYS) {
    const value = iso[key] ?? iso.properties?.[key]
    if (value !== undefined && value !== null && `${value}` !== '') {
      metadata[key] = `${value}`
    }
  }
  return metadata
}

/** Best-effort delete used both on internal failure and by the create modal's cleanup path. */
export const cleanupIsoBootVolume = async (volumeId?: string): Promise<void> => {
  if (!volumeId) {
    return
  }
  try {
    await ApiClient.getInstance().cinder.deleteVolume(volumeId)
  } catch {
    // Best effort — never mask the original failure with a cleanup error. If Nova already
    // claimed the volume before failing, this delete may no-op (409 in-use); that is fine.
  }
}

/** Wait until the freshly created boot volume is `available`; throw on error status or timeout. */
const waitUntilAvailable = async (volumeId: string): Promise<void> => {
  const { cinder } = ApiClient.getInstance()
  const deadline = Date.now() + DEFAULT_TIMEOUT_MS
  while (Date.now() < deadline) {
    try {
      const vol = await cinder.getPf9Volume({ id: volumeId })
      const status = vol?.status?.toLowerCase()
      if (status === 'available') {
        return
      }
      if (status?.startsWith('error')) {
        throw new Error(`ISO boot volume failed with status "${status}"`)
      }
    } catch (error) {
      // Re-throw the terminal error-status case; ignore transient read errors and retry.
      if (error instanceof Error && error.message.startsWith('ISO boot volume failed')) {
        throw error
      }
    }
    await sleep(DEFAULT_POLL_INTERVAL_MS)
  }
  throw new Error('ISO boot volume did not become available in time. Please try again.')
}

interface PrepareIsoBootVolumeParams {
  name: string
  volumeSize: string
  volumeType: string
  installerIso: any[]
}

/**
 * IBV-FR-001/003/004/005: create the boot volume with the wizard's size + volume type,
 * wait for it, mark it bootable, and stamp the installer ISO's firmware properties onto
 * it. Returns the new volume id. On any failure the volume is deleted so no orphan is
 * left behind, and the error is rethrown for the caller to surface.
 */
export const prepareIsoBootVolume = async ({
  name,
  volumeSize,
  volumeType,
  installerIso,
}: PrepareIsoBootVolumeParams): Promise<string> => {
  const { cinder } = ApiClient.getInstance()
  const size = Math.max(1, Math.ceil(Number(volumeSize) || 0))

  const volume = await cinder.createVolume({
    volume: {
      size,
      name: `${name || 'vm'}-boot`,
      volume_type: volumeType || undefined,
    },
  })

  if (!volume?.id) {
    // Cinder's createVolume returns the error object instead of throwing (see Cinder.ts),
    // so re-throw it when it looks like an error. This lets the create modal surface the real
    // reason (quota exceeded, invalid volume type, no backend) instead of a generic message.
    if (volume instanceof Error || volume?.message || volume?.response) {
      throw volume
    }
    throw new Error('Failed to create ISO boot volume')
  }

  try {
    await waitUntilAvailable(volume.id)
    // Nova rejects a non-bootable volume as boot_index=0, so mark it bootable.
    await cinder.setBootable(volume.id, true)
    const metadata = pickIsoFirmwareMetadata(installerIso?.[0])
    if (Object.keys(metadata).length > 0) {
      await cinder.setVolumeImageMetadata({ id: volume.id, metadata })
    }
    return volume.id
  } catch (error) {
    await cleanupIsoBootVolume(volume.id)
    throw error
  }
}
